import { Metafield } from '@/generated/graphql';
import {
  PriceAdjustments,
  PreorderInfo,
  PreorderShopInfo,
  SellingPlanAllocationNode,
  ShopInfoNode,
  SellingPlan,
} from '@/lib/types/stoq';
import {
  formatPreorderDateFromPreorderName,
  formatShippingDate,
  adjustPreorderDate,
} from '../date';
import { SellingPlanInfo } from './types';

type PreorderMetafield = {
  isPreorder: boolean | null;
  preorderDiscount: number | null;
  preorderDate: string | null;
};
export function determinePreorderStatus(metafields: Metafield[]) {
  const preorderStatus: PreorderMetafield = {
    isPreorder: false,
    preorderDiscount: null,
    preorderDate: null,
  };

  if (!metafields) return preorderStatus;

  metafields.forEach((metafield) => {
    if (metafield?.namespace === 'custom') {
      switch (metafield.key) {
        case 'is_preorder':
          //preorderStatus.isPreorder = metafield.value === 'true';
          preorderStatus.isPreorder = false;
          break;
        case 'preorder_date':
          preorderStatus.preorderDate = metafield.value;
          break;
        case 'preorder_discount':
          preorderStatus.preorderDiscount =
            metafield.value !== null ? parseFloat(metafield.value) : null;
          break;
        default:
          break;
      }
    }
  });

  return preorderStatus;
}

export function hasWebsiteTrue(metafields: any[]): boolean {
  return metafields?.some(
    ({ node }) =>
      node?.key === 'website_true' &&
      (node?.value?.toLowerCase() === 'true' || node?.value?.toLowerCase() === 'undefined')
  );
}

export function getPreorderInfoFromAllocation(
  allocPriceAdjustment: PriceAdjustments,
  allocSellingPlan: SellingPlan
): PreorderInfo {
  // Extract each price field or default to null
  const planPrice = allocPriceAdjustment?.price?.amount ?? '0';
  const compareAtPrice = allocPriceAdjustment?.compareAtPrice?.amount ?? '0';
  const perDeliveryPrice = allocPriceAdjustment?.perDeliveryPrice?.amount ?? '0';

  const sellingPlanName = allocSellingPlan?.name;
  const parts = sellingPlanName?.split('-');
  let preorderDiscount = null;
  let expectedInHouseDate = null;
  let planIsPreorder = false;

  const patternForInternalNameWithWks = /^Preorder(?:-[A-Z0-9]+)*-\d{1,2}wks?-?-\d+$/i;
  const patternForInternalNameWithDate = /^Preorder(?:-[A-Z0-9]+)*-\d{4}-\d+$/i;
  if (
    patternForInternalNameWithWks.test(sellingPlanName) ||
    patternForInternalNameWithDate.test(sellingPlanName)
  ) {
    const len = parts.length;
    const discountPart = parts[len - 1];
    const datePart = parts[len - 2];

    const discountNum = Number(discountPart);
    preorderDiscount = Number.isNaN(discountNum) ? null : String(discountNum);
    expectedInHouseDate = formatPreorderDateFromPreorderName(datePart);
    planIsPreorder = sellingPlanName?.toLowerCase().trim().includes('preorder') || false;
  }

  const sellingPlanId = allocSellingPlan?.id ? allocSellingPlan.id.replace(/\D/g, '') : '';

  return {
    planPrice,
    compareAtPrice,
    perDeliveryPrice,
    planIsPreorder,
    sellingPlanId,
    preorderDiscount,
    expectedInHouseDate,
  };
}

export function getPreorderInfoFromShop(shopData: any, sellingPlanId: string): PreorderShopInfo {
  if (!shopData) return { expectedInHouseDate: null, preorderDiscount: null };
  const matchedPlan = shopData.find(
    (plan) => plan.shopify_selling_plan_id === Number(sellingPlanId)
  );

  const shippingText = matchedPlan?.shipping_text;
  const expectedInHouseDate = formatShippingDate(shippingText);

  const preorderDiscount = matchedPlan?.pricing_amount;

  return {
    expectedInHouseDate,
    preorderDiscount,
  };
}

export function determinePreorderSellingPlanInfo(
  allocationNode: SellingPlanAllocationNode,
  shopData: ShopInfoNode
): SellingPlanInfo {
  // Grab the first allocation node, if any
  const priceAdjustment = allocationNode?.priceAdjustments?.[0];
  const sellingPlan = allocationNode?.sellingPlan;
  const { sellingPlanId, planIsPreorder, preorderDiscount, expectedInHouseDate } =
    getPreorderInfoFromAllocation(priceAdjustment, sellingPlan);

  // Adjust the preorder date if it's in the past
  const adjustedExpectedInHouseDate = adjustPreorderDate(expectedInHouseDate);

  return {
    preorderDiscount,
    planIsPreorder,
    sellingPlanId,
    expectedInHouseDate: adjustedExpectedInHouseDate,
  };
}
